import type { DocumentNode } from 'graphql'
import type { MutationOperationAndVariables } from '@/lib/mutationHelpers'
import { formatISO } from 'date-fns'
import type { CompiledQuery } from 'kysely'
import { DataBase } from '@/lib/kysely'
import type { DB } from '@/lib/kysely-d'

type M2MMutationParams = {
  fromColumn: string
  fromId: string
  toColumn: string
  beforeToIds: string[]
  afterToIds: string[]
  insertQuery: DocumentNode
  updateQuery: DocumentNode
  commitId: string
}

/**
 * example:
 *   beforeIds: [0, 1, 2]
 *   afterIds:  [1, 4, 5]
 *   =>
 *   diffIds:
 *     added:   [4, 5]
 *     deleted: [0]
 *   =>
 *   insert: [{ $fromColumn:$fromId, $toColumn:4 }, { $fromColumn:$fromId, $toColumn:5 }]
 *   update: [{ $fromColumn:$fromId, $toColumn:0, deleted_at:now() }]
 */
export function buildM2MMutations(params: M2MMutationParams): MutationOperationAndVariables[] {
  const diffIds = diff(params.beforeToIds, params.afterToIds)
  const mutations: MutationOperationAndVariables[] = []
  const deletedAt = formatISO(new Date())

  diffIds.deleted.forEach((toId) => {
    const variables = {
      [params.fromColumn]: params.fromId,
      [params.toColumn]: toId,
      deletedAt: deletedAt,
      commit_id: params.commitId
    }
    mutations.push({
      mutation: params.updateQuery,
      variables
    })
  })

  const insertVariables: any[] = []
  diffIds.added.forEach((toId) => {
    insertVariables.push({
      commit_id: params.commitId,
      [params.fromColumn]: params.fromId,
      [params.toColumn]: toId
    })
  })
  mutations.push({
    mutation: params.insertQuery,
    variables: { input: insertVariables }
  })
  return mutations
}

type AllTableColumns = { [K in keyof DB]: keyof DB[K] }
type AnyTableColumn = AllTableColumns[keyof AllTableColumns]
type M2MLocalDBParams = {
  mappingTable: keyof DB
  fromColumn: AnyTableColumn
  fromId: string
  toColumn: AnyTableColumn
  beforeToIds: string[]
  afterToIds: string[]
  tenantId: string
}

export function buildM2MLocalDBquery(params: M2MLocalDBParams): CompiledQuery[] {
  const diffIds = diff(params.beforeToIds, params.afterToIds)
  const queries: CompiledQuery[] = []

  diffIds.deleted.forEach((toId) => {
    const sql = DataBase.deleteFrom(params.mappingTable)
      .where(params.fromColumn, '=', params.fromId)
      .where(params.toColumn, '=', toId)
      .where('tenant_id', '=', params.tenantId)
      .compile()
    queries.push(sql)
  })
  diffIds.added.forEach((toId) => {
    const sqliteParams = {
      [params.fromColumn]: params.fromId,
      [params.toColumn]: toId,
      tenant_id: params.tenantId
    }
    queries.push(DataBase.insertInto(params.mappingTable).values(sqliteParams).compile())
  })
  return queries
}

type Diff = {
  added: string[]
  deleted: string[]
}

function diff(before: string[], after: string[]): Diff {
  const deleted = before.filter((x) => !after.includes(x))
  const added = after.filter((x) => !before.includes(x))
  return { deleted, added }
}
